/**
 * EMPLOYEE INFO STEP - ENHANCED
 * 
 * Features:
 * - All employee personal details
 * - Filing Status (Single/Married/Head of Household)
 * - Direct Deposit Details (Bank name, masked account)
 * - Employee ID and SSN
 * - ZIP Code Auto-fill (City/State lookup)
 */

import React, { useState, useCallback, useEffect } from 'react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { toast } from 'sonner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { User, CreditCard, Building, Shield, MapPin, Loader2 } from 'lucide-react';
import { STATE_TAX_DATA } from '../../utils/taxCalculations';
import { lookupZipCode } from '../../utils/zipCodeLookup';
import FieldGate from '../FieldGate';
import AddressAutocomplete from '../AddressAutocomplete';

const US_STATES = Object.entries(STATE_TAX_DATA).map(([code, data]) => ({
  code,
  name: data.name
})).sort((a, b) => a.name.localeCompare(b.name));

const FILING_STATUSES = [
  { value: 'single', label: 'Single', description: 'Unmarried or legally separated' },
  { value: 'married', label: 'Married Filing Jointly', description: 'Married, filing together' },
  { value: 'married_separate', label: 'Married Filing Separately', description: 'Married, filing alone' },
  { value: 'head_of_household', label: 'Head of Household', description: 'Unmarried with dependent' }
];

export default function EmployeeInfoStep({
  formData,
  updateFormData,
  missingFields = [],
  skippedFields = {},
  onToggleSkip,
  attemptedNext = false,
}) {
  const gateProps = (key) => ({
    fieldKey: key,
    missingFields,
    skippedFields,
    onToggleSkip,
    attemptedNext,
  });
  const [isLookingUpZip, setIsLookingUpZip] = useState(false);

  // Back-populate First/Last fields from a single employeeName loaded from a
  // saved client (older clients only stored the combined name). Runs once when
  // employeeName arrives but first/last are still empty.
  useEffect(() => {
    if (!formData.employeeFirstName && !formData.employeeLastName && formData.employeeName) {
      const parts = (formData.employeeName || '').trim().split(/\s+/);
      const first = (parts[0] || '').toUpperCase();
      const last = parts.slice(1).join(' ').toUpperCase();
      if (first || last) {
        updateFormData({ employeeFirstName: first, employeeLastName: last });
      }
    }
  }, [formData.employeeName]);

  // ZIP Code auto-fill handler
  const handleZipCodeChange = useCallback(async (zipCode) => {
    updateFormData({ employeeZip: zipCode });
    
    // Only lookup if ZIP is 5 digits
    if (zipCode && /^\d{5}$/.test(zipCode)) {
      setIsLookingUpZip(true);
      try {
        const result = await lookupZipCode(zipCode);
        if (result) {
          updateFormData({ 
            employeeCity: result.city,
            employeeState: result.stateCode
          });
          toast.success(`Auto-filled: ${result.city}, ${result.stateCode}`);
        }
      } catch (error) {
        console.error('ZIP lookup error:', error);
      } finally {
        setIsLookingUpZip(false);
      }
    }
  }, [updateFormData]);

  // Mask SSN display (only show last 4 digits)
  const formatSSN = (value) => {
    const digits = value.replace(/\D/g, '').slice(0, 9);
    if (digits.length <= 3) return digits;
    if (digits.length <= 5) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
    return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
  };

  // Mask account number (show only last 4 digits)
  const maskAccountNumber = (value) => {
    if (!value || value.length <= 4) return value;
    return '•'.repeat(value.length - 4) + value.slice(-4);
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Employee First Name */}
        <FieldGate {...gateProps('employeeName')}>
          <Label className="text-zinc-300">First Name *</Label>
          <Input
            value={formData.employeeFirstName || ''}
            onChange={(e) => {
              const first = e.target.value.toUpperCase();
              const last = formData.employeeLastName || '';
              updateFormData({
                employeeFirstName: first,
                employeeName: `${first} ${last}`.trim(),
              });
            }}
            placeholder="JOHN"
            className="mt-1 bg-zinc-800 border-zinc-600 text-white uppercase"
            required
            data-testid="employee-first-name-input"
          />
        </FieldGate>

        {/* Employee Last Name */}
        <div>
          <Label className="text-zinc-300">Last Name *</Label>
          <Input
            value={formData.employeeLastName || ''}
            onChange={(e) => {
              const last = e.target.value.toUpperCase();
              const first = formData.employeeFirstName || '';
              updateFormData({
                employeeLastName: last,
                employeeName: `${first} ${last}`.trim(),
              });
            }}
            placeholder="DOE"
            className="mt-1 bg-zinc-800 border-zinc-600 text-white uppercase"
            required
            data-testid="employee-last-name-input"
          />
        </div>
      </div>

      {/* Employee ID + SSN row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label className="text-zinc-300">Employee ID</Label>
          <Input
            value={formData.employeeId || ''}
            onChange={(e) => updateFormData({ employeeId: e.target.value.toUpperCase() })}
            placeholder="EMP-001"
            className="mt-1 bg-zinc-800 border-zinc-600 text-white uppercase"
            data-testid="employee-id-input"
          />
        </div>

        {/* SSN */}
        <FieldGate {...gateProps('employeeSSN')}>
          <Label className="text-zinc-300">Social Security Number (Last 4)</Label>
          <Input
            value={formData.employeeSSN || ''}
            onChange={(e) => updateFormData({ employeeSSN: e.target.value.replace(/\D/g, '').slice(0, 4) })}
            placeholder="6789"
            maxLength={4}
            className="mt-1 bg-zinc-800 border-zinc-600 text-white"
            data-testid="employee-ssn-input"
          />
          <p className="text-xs text-zinc-500 mt-1">
            Will display as XXX-XX-{formData.employeeSSN || '****'}
          </p>
        </FieldGate>
      </div>

      {/* Filing Status */}
      <div>
        <Label className="text-zinc-300 mb-3 block">Filing Status</Label>
        <RadioGroup
          value={formData.filingStatus || 'single'}
          onValueChange={(value) => updateFormData({ filingStatus: value })}
          className="grid grid-cols-1 md:grid-cols-2 gap-3"
        >
          {FILING_STATUSES.map((status) => (
            <div 
              key={status.value}
              className={`flex items-center space-x-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                formData.filingStatus === status.value 
                  ? 'border-amber-500 bg-amber-500/10' 
                  : 'border-zinc-700 bg-zinc-800/50 hover:border-zinc-600'
              }`}
            >
              <RadioGroupItem value={status.value} id={status.value} />
              <div>
                <Label htmlFor={status.value} className="text-white cursor-pointer">{status.label}</Label>
                <p className="text-xs text-zinc-500">{status.description}</p>
              </div>
            </div>
          ))}
        </RadioGroup>
      </div>

      {/* Exemptions */}
      <div>
        <Label className="text-zinc-300">Federal Allowances/Exemptions</Label>
        <Select 
          value={String(formData.exemptions || 1)}
          onValueChange={(value) => updateFormData({ exemptions: parseInt(value) })}
        >
          <SelectTrigger className="mt-1 bg-zinc-800 border-zinc-600 text-white">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => (
              <SelectItem key={num} value={String(num)}>
                {num} {num === 1 ? 'allowance' : 'allowances'}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-xs text-zinc-500 mt-1">More allowances = less tax withheld</p>
      </div>

      {/* Direct Deposit Section */}
      <div className="border-t border-zinc-700 pt-6">
        <div className="flex items-center justify-between mb-4">
          <Label className="text-zinc-300 flex items-center gap-2">
            <CreditCard className="w-4 h-4 text-amber-400" />
            Direct Deposit
          </Label>
          <Switch
            checked={formData.directDeposit !== false}
            onCheckedChange={(checked) => updateFormData({ directDeposit: checked })}
            data-testid="direct-deposit-toggle"
          />
        </div>
        
        {formData.directDeposit !== false && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 ml-6">
            <div>
              <Label className="text-zinc-300">Bank Name</Label>
              <Input
                value={formData.bankName || ''}
                onChange={(e) => updateFormData({ bankName: e.target.value.toUpperCase() })}
                placeholder="CHASE BANK"
                className="mt-1 bg-zinc-800 border-zinc-600 text-white uppercase"
                data-testid="bank-name-input"
              />
            </div>
            <div>
              <Label className="text-zinc-300">Account Number (Last 4 digits)</Label>
              <Input
                value={formData.accountNumber || ''}
                onChange={(e) => updateFormData({ accountNumber: e.target.value.replace(/\D/g, '').slice(0, 4) })}
                placeholder="4521"
                maxLength={4}
                className="mt-1 bg-zinc-800 border-zinc-600 text-white"
                data-testid="account-number-input"
              />
              <p className="text-xs text-zinc-500 mt-1">
                Will display as ****{formData.accountNumber || '****'}
              </p>
            </div>
          </div>
        )}
        
        {formData.directDeposit === false && (
          <p className="text-sm text-zinc-400 ml-6">
            Payment will be shown as "Check" on the paystub
          </p>
        )}
      </div>

      {/* Employee Address */}
      <div className="border-t border-zinc-700 pt-6">
        <h4 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Building className="w-5 h-5 text-amber-400" />
          Employee Address
        </h4>
        
        <div className="space-y-4">
          <FieldGate {...gateProps('employeeAddress1')}>
            <Label className="text-zinc-300">Street Address 1 *</Label>
            <AddressAutocomplete
              label={null}
              value={formData.employeeAddress1 || ''}
              onChange={(e) => updateFormData({ employeeAddress1: (typeof e === 'string' ? e : e.target.value).toUpperCase() })}
              onSelect={({ line, city, state, zip }) => {
                updateFormData({
                  employeeAddress1: (line || '').toUpperCase(),
                  employeeCity: (city || formData.employeeCity || '').toUpperCase(),
                  employeeState: (state || formData.employeeState || '').toUpperCase(),
                  employeeZip: zip || formData.employeeZip || '',
                });
              }}
              placeholder="Start typing address…"
              testId="employee-address1"
            />
          </FieldGate>

          <div>
            <Label className="text-zinc-300">Street Address 2</Label>
            <Input
              value={formData.employeeAddress2 || ''}
              onChange={(e) => updateFormData({ employeeAddress2: e.target.value.toUpperCase() })}
              placeholder="APT 4B"
              className="mt-1 bg-zinc-800 border-zinc-600 text-white uppercase"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <FieldGate {...gateProps('employeeCity')}>
              <Label className="text-zinc-300">City *</Label>
              <Input
                value={formData.employeeCity || ''}
                onChange={(e) => updateFormData({ employeeCity: e.target.value.toUpperCase() })}
                placeholder="LOS ANGELES"
                className="mt-1 bg-zinc-800 border-zinc-600 text-white uppercase"
                required
                data-testid="employee-city"
              />
            </FieldGate>

            <FieldGate {...gateProps('employeeState')}>
              <Label className="text-zinc-300">State *</Label>
              <Select
                value={formData.employeeState || 'CA'}
                onValueChange={(value) => updateFormData({ employeeState: value })}
              >
                <SelectTrigger className="mt-1 bg-zinc-800 border-zinc-600 text-white" data-testid="employee-state">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {US_STATES.map((state) => (
                    <SelectItem key={state.code} value={state.code}>
                      {state.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FieldGate>

            {/* Zip Code - AUTO-FILL City/State */}
            <FieldGate {...gateProps('employeeZip')}>
              <Label className="text-zinc-300 flex items-center gap-2">
                Zip Code *
                {isLookingUpZip && <Loader2 className="w-3 h-3 animate-spin text-amber-400" />}
              </Label>
              <Input
                value={formData.employeeZip || ''}
                onChange={(e) => {
                  const zip = e.target.value.replace(/\D/g, '').slice(0, 5);
                  handleZipCodeChange(zip);
                }}
                placeholder="ENTER ZIP TO AUTO-FILL"
                className="mt-1 bg-zinc-800 border-amber-500/50 text-white"
                maxLength={5}
                required
                data-testid="employee-zip"
              />
              <p className="text-xs text-amber-400 mt-1 flex items-center gap-1">
                <MapPin className="w-3 h-3" />
                Auto-fills City & State
              </p>
            </FieldGate>
          </div>
        </div>
      </div>

      {/* Preview Card */}
      <div className="mt-6 p-4 bg-zinc-800/50 rounded-lg border border-zinc-700">
        <h4 className="text-sm font-semibold text-zinc-400 mb-3 flex items-center gap-2">
          <User className="w-4 h-4" />
          Employee Preview
        </h4>
        <div className="space-y-1">
          <p className="font-bold text-white">{formData.employeeName || 'Employee Name'}</p>
          <p className="text-sm text-zinc-400">
            {formData.employeeAddress1 || '456 Oak Avenue'}
          </p>
          <p className="text-sm text-zinc-400">
            {formData.employeeCity || 'City'}, {formData.employeeState || 'CA'} {formData.employeeZip || '00000'}
          </p>
          <p className="text-sm text-zinc-400 mt-2">
            SSN: XXX-XX-{formData.employeeSSN || '****'}
          </p>
          {formData.employeeId && (
            <p className="text-sm text-zinc-400">
              ID: {formData.employeeId}
            </p>
          )}
          <p className="text-sm text-amber-400 mt-2">
            Filing: {FILING_STATUSES.find(s => s.value === (formData.filingStatus || 'single'))?.label}
          </p>
          {formData.directDeposit !== false && formData.bankName && (
            <p className="text-sm text-green-400">
              {formData.bankName} ****{formData.accountNumber || '****'}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
